/**
 * Leads — Screen 5
 * Smart-list tabs, filter row, table, lead drawer.
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLeads, fetchLead, patchLead } from '../../lib/apiHelpers.js';
import { StageChip, STAGE_CONFIG } from '../../components/StageChip.js';
import { AvatarInitials } from '../../components/AvatarInitials.js';
import { Toggle, AiToggle } from '../../components/Toggle.js';
import type { ApiLead, LeadStage, LeadSource } from '@contactrate/shared';
import { usePortal } from '../../portal/PortalContext.js';

const STAGES: { value: LeadStage; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'called', label: 'Called' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'transfer', label: 'Live Transfer' },
  { value: 'booked', label: 'Booked' },
  { value: 'presented', label: 'Presented' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'closed', label: 'Closed' },
];

const DISPOSITION_STAGES: { value: LeadStage; label: string }[] = [
  { value: 'called', label: 'Called' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'booked', label: 'Booked' },
  { value: 'presented', label: 'Presented' },
  { value: 'closed', label: 'Closed' },
];

type SmartTab = 'all' | 'unworked' | 'hot' | 'booked' | 'dq';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Lead drawer ───────────────────────────────────────────────────────────────

function LeadDrawer({
  leadId,
  aiName,
  onClose,
}: {
  leadId: string;
  aiName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: lead } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => fetchLead(leadId),
  });

  const [tagInput, setTagInput] = useState('');

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => patchLead(leadId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  // Body scroll lock
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleRemoveTag = (tag: string) => {
    if (!lead) return;
    patch.mutate({ tags: (lead.tags ?? []).filter((t) => t !== tag) });
  };

  const handleAddTag = () => {
    if (!lead || !tagInput.trim()) return;
    patch.mutate({ tags: [...(lead.tags ?? []), tagInput.trim()] });
    setTagInput('');
  };

  const stageColor = (s: LeadStage) => STAGE_CONFIG[s]?.color ?? '#64748B';

  if (!lead) {
    return (
      <div
        className="fixed inset-0 z-50 flex justify-end"
        style={{ background: 'rgba(13,23,41,0.4)' }}
      >
        <div className="bg-surface h-full w-[440px] flex items-center justify-center">
          <div className="text-muted">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 z-50"
        style={{ background: 'rgba(13,23,41,0.4)' }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 bg-surface flex flex-col overflow-y-auto"
        style={{ width: 440, boxShadow: '-4px 0 24px rgba(13,23,41,0.1)' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-5 py-4 flex items-center gap-3 bg-surface flex-shrink-0"
          style={{ borderBottom: '1px solid #F0F3F8', zIndex: 1 }}
        >
          <AvatarInitials name={`${lead.firstName} ${lead.lastName}`} size={36} />
          <div className="flex-1 min-w-0">
            <p
              className="text-[15px] font-semibold text-ink truncate"
              style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
            >
              {lead.firstName} {lead.lastName}
            </p>
            <p className="text-[11.5px] text-muted">
              {lead.phone} · {lead.state} · {lead.source}
            </p>
          </div>
          <AiToggle
            aiActive={!lead.aiPaused}
            onChange={(ai) => patch.mutate({ ai_paused: !ai })}
          />
          <button
            onClick={onClose}
            className="p-1.5 rounded-[8px] text-muted hover:bg-surface-alt transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 px-5 py-4 flex flex-col gap-5">
          {/* Disposition chips */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-2">Stage</p>
            <div className="flex flex-wrap gap-2">
              {DISPOSITION_STAGES.map(({ value, label }) => {
                const isActive = lead.stage === value;
                const color = stageColor(value);
                return (
                  <button
                    key={value}
                    onClick={() => patch.mutate({ stage: value })}
                    className="px-3 py-1.5 rounded-chip text-[11.5px] font-semibold transition-colors"
                    style={
                      isActive
                        ? { background: color, color: 'white' }
                        : { background: `${color}18`, color, border: `1px solid ${color}40` }
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact fields */}
          <div className="bg-surface rounded-card shadow-card p-4 flex flex-col gap-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Contact</p>
            {[
              { label: 'Phone', val: lead.phone, href: `tel:${lead.phone}` },
              { label: 'Email', val: lead.email ?? '—' },
              { label: 'State', val: lead.state ?? '—' },
              { label: 'Zip', val: lead.zip ?? '—' },
            ].map(({ label, val, href }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-[10.5px] text-muted w-14 flex-shrink-0">{label}</span>
                {href ? (
                  <a href={href} className="text-[12px] font-medium text-blue-DEFAULT hover:text-blue-hover">
                    {val}
                  </a>
                ) : (
                  <span className="text-[12px] text-ink font-medium">{val}</span>
                )}
              </div>
            ))}
          </div>

          {/* Custom fields */}
          {Object.keys(lead.customFields ?? {}).length > 0 && (
            <div className="bg-surface rounded-card shadow-card p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Custom fields</p>
                <button className="text-[10.5px] text-blue-DEFAULT hover:text-blue-hover font-medium">
                  Manage →
                </button>
              </div>
              {Object.entries(lead.customFields ?? {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-[10.5px] text-muted w-28 flex-shrink-0">{k}</span>
                  <span className="text-[12px] text-ink font-medium">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tags */}
          <div className="bg-surface rounded-card shadow-card p-4 flex flex-col gap-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {(lead.tags ?? []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[11px] font-medium"
                  style={{ background: '#F2F4F7', border: '1px solid #E5EAF2', color: '#64748B' }}
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="text-muted hover:text-red-DEFAULT transition-colors"
                  >
                    ×
                  </button>
                </span>
              ))}
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="+ Add tag"
                  className="text-[11.5px] text-ink bg-surface-alt px-2 py-0.5 rounded-[6px] outline-none border border-border focus:border-blue-DEFAULT"
                  style={{ width: 90 }}
                />
              </div>
            </div>
          </div>

          {/* Deal value */}
          <div className="bg-surface rounded-card shadow-card p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-1">Deal value</p>
            <p
              className="text-[22px] font-medium text-ink"
              style={{ fontFamily: '"DM Mono", monospace' }}
            >
              ${lead.dealValue.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="sticky bottom-0 px-5 py-3 flex gap-2 bg-surface flex-shrink-0"
          style={{ borderTop: '1px solid #F0F3F8' }}
        >
          <button
            className="flex-1 py-2 rounded-btn text-[12.5px] font-semibold text-white"
            style={{ background: '#2E66E0' }}
          >
            Open conversation
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-btn text-[12.5px] font-medium text-ink"
            style={{ background: '#F2F4F7' }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Leads Page ───────────────────────────────────────────────────────────

interface LeadsPageProps {
  aiName: string;
}

export function LeadsPage({ aiName }: LeadsPageProps) {
  const [tab, setTab] = useState<SmartTab>('all');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
  const qc = useQueryClient();
  const { caps, guardWrite, flash } = usePortal();

  const { data, isLoading } = useQuery({
    queryKey: ['leads', { stage: stageFilter, source: sourceFilter, search, page, pageSize: 50 }],
    queryFn: () => fetchLeads({
      stage: stageFilter || undefined,
      source: (sourceFilter || undefined) as LeadSource | undefined,
      search: search || undefined,
      page,
      pageSize: 50,
    }),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      if (!caps.canDispositionLeads && !caps.canToggleAiPerLead) {
        return Promise.reject(new Error('managed'));
      }
      // stage disposition needs CRM; ai pause needs CRM toggle flag
      if (body.stage !== undefined && !caps.canDispositionLeads) {
        return Promise.reject(new Error('managed'));
      }
      if (body.aiPaused !== undefined && !caps.canToggleAiPerLead) {
        return Promise.reject(new Error('managed'));
      }
      return patchLead(id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => {
      flash('Managed mode is view-only — unlock Workspace CRM to edit leads.');
    },
  });

  const allLeads = data?.data ?? [];

  // Apply smart tab filter locally
  const filtered = allLeads.filter((l) => {
    if (tab === 'unworked') return l.stage === 'new' && !l.aiPaused;
    if (tab === 'hot') return ['contacted', 'transfer'].includes(l.stage);
    if (tab === 'booked') return l.stage === 'booked';
    if (tab === 'dq') return l.dnc === true;
    return true;
  });

  const total = data?.total ?? 0;

  const SMART_TABS: { key: SmartTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'unworked', label: 'Unworked' },
    { key: 'hot', label: 'Hot' },
    { key: 'booked', label: 'Booked' },
    { key: 'dq', label: 'DQ' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1
            className="text-[22px] font-bold text-ink tracking-[-0.01em]"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            Leads
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-[13.5px] text-muted">
              <span style={{ fontFamily: '"DM Mono", monospace' }}>{total}</span> leads · webhook, Facebook, and CSV intake
            </p>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-chip text-[10.5px] font-semibold"
              style={{ background: 'rgba(21,150,98,0.08)', color: '#159662', border: '1px solid rgba(21,150,98,0.2)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-DEFAULT inline-block" />
              Webhook live · last lead 4m ago
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium text-ink transition-colors"
            style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
          >
            Import CSV
          </button>
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white transition-colors"
            style={{ background: '#2E66E0' }}
          >
            Add lead
          </button>
        </div>
      </div>

      {/* Smart-list tabs */}
      <div
        className="flex items-center border-b-2 mb-4"
        style={{ borderColor: '#F0F3F8' }}
      >
        {SMART_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="pb-2 pt-1 px-3 text-[12.5px] font-medium relative transition-colors"
            style={{ color: tab === key ? '#0D1729' : '#64748B' }}
          >
            {label}
            {tab === key && (
              <span
                className="absolute bottom-[-2px] left-0 right-0 h-0.5 rounded-full"
                style={{ background: '#0D1729' }}
              />
            )}
          </button>
        ))}
        <div className="flex-1" />
        <button className="pb-2 text-[11.5px] font-medium text-blue-DEFAULT hover:text-blue-hover transition-colors px-2">
          + Save view
        </button>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-4">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-[10px] flex-1 max-w-xs"
          style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted flex-shrink-0">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search leads…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 bg-transparent text-[12.5px] text-ink placeholder-muted outline-none min-w-0"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-[10px] text-[12px] text-ink bg-surface border border-border outline-none"
        >
          <option value="">Stage</option>
          {STAGES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-[10px] text-[12px] text-ink bg-surface border border-border outline-none"
        >
          <option value="">Source</option>
          <option value="webhook">Webhook</option>
          <option value="facebook">Facebook</option>
          <option value="csv">CSV</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-card shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr style={{ borderBottom: '1px solid #F0F3F8' }}>
                {['Created', 'First name', 'Last name', 'Phone', 'Email', 'Stage', 'Source', 'Automation', 'AI', 'Last activity'].map((col) => (
                  <th
                    key={col}
                    className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-muted text-[12.5px]">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-muted text-[12.5px]">No leads found</td>
                </tr>
              ) : (
                filtered.map((lead) => (
                  <tr
                    key={lead.id}
                    className="cursor-pointer"
                    style={{ borderBottom: '1px solid #F0F3F8' }}
                    onClick={() => setDrawerLeadId(lead.id)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = '#FAFBFC';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = '';
                    }}
                  >
                    <td
                      className="px-4 py-2.5 text-[11.5px] text-muted whitespace-nowrap"
                      style={{ fontFamily: '"DM Mono", monospace' }}
                    >
                      {fmtDate(lead.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-[12.5px] font-medium text-ink">{lead.firstName}</td>
                    <td className="px-4 py-2.5 text-[12.5px] font-medium text-ink">{lead.lastName}</td>
                    <td
                      className="px-4 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <a
                        href={`tel:${lead.phone}`}
                        className="text-[12px] font-medium transition-colors hover:underline"
                        style={{ color: '#2E66E0', fontFamily: '"DM Mono", monospace' }}
                      >
                        {lead.phone}
                      </a>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-muted max-w-[140px] truncate">
                      {lead.email ?? '—'}
                    </td>
                    <td
                      className="px-4 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={lead.stage}
                        onChange={(e) => {
                          e.stopPropagation();
                          patchMutation.mutate({ id: lead.id, body: { stage: e.target.value } });
                        }}
                        className="rounded-chip text-[10.5px] font-semibold px-2 py-0.5 border-0 outline-none cursor-pointer appearance-none"
                        style={{
                          color: STAGE_CONFIG[lead.stage as LeadStage]?.color ?? '#64748B',
                          background: STAGE_CONFIG[lead.stage as LeadStage]?.bg ?? '#F2F4F7',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {STAGES.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-muted capitalize">{lead.source}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[11px]" style={{ color: '#6D4AEA' }}>
                        ✦ Fresh Lead · d1
                      </span>
                    </td>
                    <td
                      className="px-4 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Toggle
                        size="sm"
                        checked={!lead.aiPaused}
                        onChange={(ai) => {
                          patchMutation.mutate({ id: lead.id, body: { ai_paused: !ai } });
                        }}
                        trackOn="#159662"
                        trackOff="#D8DFEA"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-[11.5px] text-muted max-w-[160px] truncate">
                      {fmtDate(lead.updatedAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(data?.hasMore || page > 1) && (
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{ borderTop: '1px solid #F0F3F8' }}
          >
            <p className="text-[11.5px] text-muted">
              Showing {filtered.length} of{' '}
              <span style={{ fontFamily: '"DM Mono", monospace' }}>{total}</span> leads
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium transition-colors disabled:opacity-40"
                style={{ background: '#F2F4F7', color: '#64748B' }}
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!data?.hasMore}
                className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium transition-colors disabled:opacity-40"
                style={{ background: '#F2F4F7', color: '#64748B' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lead drawer */}
      {drawerLeadId && (
        <LeadDrawer
          leadId={drawerLeadId}
          aiName={aiName}
          onClose={() => setDrawerLeadId(null)}
        />
      )}
    </div>
  );
}
